import ChevronLeft from '@/assets/icons/chevron-left.svg?react'
import StarIcon from '@/assets/icons/star/full.svg?react'
import Loading from '@/components/Loading/Loading'
import SkipLink from '@/components/SkipLink'
import Navbar from '@/layout/Navbar'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
// import useGrubhubValidToken from '@/lib/food-grubhub/hooks/useGrubhubValidToken'
import useMenuItemCategoryListing from '@/lib/food-grubhub/hooks/useMenuItemCategoryListing'
import useRestaurantInformationDetail from '@/lib/food-grubhub/hooks/useRestaurantInformationDetail'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import HeadTags from '@/lib/head/HeadTags'
import {FoodGrubhubDetailRoute, FoodGrubhubRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useParam from '@/utils/router/useParam'
import ReadMoreList from '@/views/FoodGrubhubRestaurantDetail/components/ReadMoreList'

// import FoodGrubhubMenuItemListing from './components/FoodGrubhubMenuItemListing/FoodGrubhubMenuItemListing'
import FoodGrubhubMenuItemListingV2 from './components/FoodGrubhubMenuItemListing/FoodGrubhubMenuItemListingV2'
import FoodGrubhubMenuItemSearch from './components/FoodGrubhubMenuItemSearch/FoodGrubhubMenuItemSearch'
import style from './FoodGrubhubRestaurantDetail.module.scss'

const EMPTY_ARRAY: string[] = []

export default function FoodGrubhubRestaurantDetail() {
  const [storageUserLocation] = useGrubhubUserAddress()
  const [restaurantId] = useParam(FoodGrubhubDetailRoute.id, 'restaurantId')
  const {q} = FoodGrubhubDetailRoute.useSearch()
  const router = useRouter()

  const {data: authData} = useGrubhubGetAuth()

  const restaurantInforamtionQuery = useMemo(
    () => ({
      restaurantId,
      hideChoiceCategories: true,
      version: 4,
      variationId: 'rtpFreeItems',
      orderType: '',
      hideUnavailableMenuItems: true,
      hideMenuItems: true,
      locationMode: 'delivery',
      location: `POINT(${storageUserLocation.longitude} ${storageUserLocation.latitude})`,
    }),
    [restaurantId, storageUserLocation.longitude, storageUserLocation.latitude],
  )
  const {data: restaurantDetail, isLoading: restaurantDetailLoading} =
    useRestaurantInformationDetail(
      restaurantInforamtionQuery,
      authData?.session_handle.access_token ?? '',
    )

  const menuItemCategoryListQuery = useMemo(
    () => ({
      orderType: 'STANDARD',
      platform: 'WEB',
      enhancedFeed: true,
      weightedItemDataIncluded: true,
      location: `POINT(${storageUserLocation.longitude} ${storageUserLocation.latitude})`,
      viewport: 'dWeb',
      restaurantId,
    }),
    [restaurantId, storageUserLocation.longitude, storageUserLocation.latitude],
  )
  const {data: menuItemCategoryListData} = useMenuItemCategoryListing(
    menuItemCategoryListQuery,
    authData?.session_handle.access_token ?? '',
  )

  const filteredNoneIdCategory = menuItemCategoryListData?.object.data.enhanced_feed.filter(
    item => item.id !== 'None' && !Number.isNaN(Number(item.id)),
  )

  const getGoBackHref = useCallback(() => {
    return router.buildLocation({
      to: FoodGrubhubRoute.fullPath,
    }).href
  }, [router])

  const restaurantAddressFormat = `${restaurantDetail?.restaurant.address?.street_address}, ${restaurantDetail?.restaurant.address?.locality}, ${restaurantDetail?.restaurant.address?.zip}`

  return (
    <div>
      <HeadTags title={restaurantDetail?.restaurant.name ?? 'Restaurant'} />
      <SkipLink title='Skip to main content' to='#main-content' />
      <Navbar withSearch variant='dark' />
      <main className={style.PageContainer} {...skipTargetProps('main-content')}>
        {restaurantDetailLoading ? (
          <Loading label='Loading restaurant detail page' />
        ) : (
          <>
            <section className={style.PageHeader}>
              <div className={style.PageHeaderContent}>
                <Link
                  to={getGoBackHref()}
                  aria-label='Go back to restaurants listing page'
                  className={style.ButtonBack}
                >
                  <ChevronLeft className={style.ButtonBackIcon} />
                  <p className={style.ButtonBackTitleMobile}>Go back</p>
                </Link>
                <div className={style.HeaderImageContainer}>
                  <div className={style.HeaderImageContent}>
                    <img
                      src={restaurantDetail?.restaurant.logo ?? ''}
                      alt=''
                      loading='lazy'
                      className={style.HeaderImage}
                    />
                  </div>
                  <div className={style.HeaderImageInfoMobile}>
                    <h1 className={style.Heading}>{restaurantDetail?.restaurant.name}</h1>
                    <ReadMoreList
                      items={restaurantDetail?.restaurant.cuisines ?? EMPTY_ARRAY}
                      maxVisible={10}
                      viewMoreTitle='Show more'
                      viewLessTitle='Show less'
                    />
                  </div>
                </div>
                <div className={style.HeaderInfoContainer}>
                  <div className={style.HeaderInfoDesktop}>
                    <h1 className={style.Heading}>{restaurantDetail?.restaurant.name}</h1>
                    <ReadMoreList
                      items={restaurantDetail?.restaurant.cuisines ?? EMPTY_ARRAY}
                      maxVisible={10}
                      viewMoreTitle='Show more'
                      viewLessTitle='Show less'
                    />
                  </div>
                  <div className={style.HeaderInfoDetail}>
                    <div className={style.BreakLine} />
                    {restaurantDetail?.restaurant.rating && (
                      <div className={style.RatingContainer}>
                        <VisuallyHidden>{`Rating: ${restaurantDetail.restaurant.rating.rating_value} out of 5 stars based on ${restaurantDetail.restaurant.rating.rating_value} reviews`}</VisuallyHidden>
                        <div className={style.RatingNumber} aria-hidden='true'>
                          {restaurantDetail.restaurant.rating.rating_value}
                        </div>
                        <StarIcon />
                        <div className={style.RatingNumber} aria-hidden='true'>
                          {'\u0028'}
                          {restaurantDetail.restaurant.rating.rating_count} reviews
                          {'\u0029'}
                        </div>
                      </div>
                    )}
                    <div className={style.Address}>
                      <VisuallyHidden>Store address: </VisuallyHidden> {restaurantAddressFormat}
                    </div>
                    <div className={style.DeliveryInfoContainer}>
                      <div className={style.DeliveryItem}>
                        <VisuallyHidden>Delivery distance: </VisuallyHidden>
                        {`\u003C ${restaurantDetail?.restaurant_availability.decimal_distance_in_miles} mi`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            {filteredNoneIdCategory && filteredNoneIdCategory.length > 0 ? (
              <>
                {q !== '' ? (
                  <FoodGrubhubMenuItemSearch
                    restaurantDetail={restaurantDetail}
                    restaurantId={restaurantId}
                    operationId={menuItemCategoryListData?.object.operation_id ?? ''}
                  />
                ) : (
                  <FoodGrubhubMenuItemListingV2
                    menuItemCategoryList={filteredNoneIdCategory}
                    restaurantDetail={restaurantDetail}
                    restaurantId={restaurantId}
                    operationId={menuItemCategoryListData?.object.operation_id ?? ''}
                  />
                )}
              </>
            ) : (
              <div className={style.ClosedContainer}>
                <h2 className={style.Heading}>Sorry, this restaurant is closed right now.</h2>
                <div role='alert'>
                  <VisuallyHidden>The restaurant is temporarily closed.</VisuallyHidden>
                  <p className={style.SubHeading}>Please check back later.</p>
                </div>
                <Link to={getGoBackHref()} className={style.Button}>
                  Go back
                </Link>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
